<template>
  <!--
    Cheque leaf print (print module, kind "cheque").
    Fields are dragged on a live preview; the cheque image underneath is a
    guide only and is never printed. Templates are stored like the other
    print templates (built-in / company / mine) under the given report key.
  -->
  <v-dialog :value="value" fullscreen hide-overlay transition="dialog-bottom-transition" @input="$emit('input', $event)">
    <div class="cs" :class="{ 'theme--dark': $vuetify.theme.isDark }" :style="rootStyle" dir="rtl">
      <!-- Header -->
      <header class="cs-head">
        <span class="cs-head__icon"><v-icon size="20">mdi-checkbook</v-icon></span>
        <div class="cs-head__titles">
          <div class="cs-head__title">{{ $t("chequePrintTitle") }}</div>
          <div class="cs-head__sub">
            {{ cheques.length > 1 ? $t("chequePrintCount", { n: faNum(cheques.length) }) : subtitle }}
          </div>
        </div>
        <label class="cs-select">
          <select :value="tplKey" :disabled="busy" @change="pickTemplate($event.target.value)">
            <optgroup v-for="g in templateGroups" :key="g.key" :label="g.label">
              <option v-for="t in g.items" :key="g.key + ':' + t.id" :value="g.key + ':' + t.id">{{ tplName(t) }}</option>
            </optgroup>
          </select>
          <v-icon size="16">mdi-chevron-down</v-icon>
        </label>
        <div class="cs-head__actions">
          <button v-if="canSave" type="button" class="cs-btn" :disabled="busy" @click="save">
            <v-icon size="16">mdi-content-save-outline</v-icon>
            <span class="cs-btn__text">{{ $t("Save") }}</span>
          </button>
          <button type="button" class="cs-btn" :disabled="busy" @click="askName">
            <v-icon size="16">mdi-content-save-plus-outline</v-icon>
            <span class="cs-btn__text">{{ $t("printSaveAs") }}</span>
          </button>
          <button type="button" class="cs-btn cs-btn--primary" :disabled="printing || !tpl" @click="print">
            <v-icon size="16">mdi-printer-outline</v-icon>
            <span class="cs-btn__text">{{ $t("print") }}</span>
          </button>
          <button type="button" class="cs-btn cs-btn--icon" :title="$t('close')" @click="close">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>
      </header>

      <div v-if="error" class="cs-alert">
        <v-icon size="16">mdi-alert-circle-outline</v-icon>{{ error }}
      </div>

      <div v-if="tpl" class="cs-main">
        <!-- Side panel -->
        <aside class="cs-side">
          <nav class="cs-tabs">
            <button type="button" :class="{ on: tab === 'fields' }" @click="tab = 'fields'">
              <v-icon size="16">mdi-format-text</v-icon>{{ $t("chequeTabFields") }}
            </button>
            <button type="button" :class="{ on: tab === 'paper' }" @click="tab = 'paper'">
              <v-icon size="16">mdi-image-size-select-actual</v-icon>{{ $t("chequeTabPaper") }}
            </button>
          </nav>

          <div class="cs-panel">
            <template v-if="tab === 'fields'">
              <p class="cs-hint"><v-icon size="14">mdi-gesture-tap-hold</v-icon>{{ $t("chequeDragHint") }}</p>
              <div class="cs-fields">
                <div
                  v-for="f in fields"
                  :key="f.key"
                  class="cs-frow"
                  :class="{ on: selected === f.key }"
                  @click="select(f.key)"
                >
                  <label class="cs-switch" @click.stop>
                    <input v-model="tpl.fields[f.key].visible" type="checkbox" /><i />
                  </label>
                  <span class="cs-frow__label">{{ f.label }}</span>
                  <span class="cs-frow__val">{{ sampleOf(f) }}</span>
                </div>
              </div>

              <div v-if="sel" class="cs-group">
                <div class="cs-label">{{ fieldLabel(selected) }}</div>
                <div class="cs-grid3">
                  <label class="cs-field"><span>{{ $t("chequeX") }}</span><input v-model.number="sel.x" type="number" step="0.5" /></label>
                  <label class="cs-field"><span>{{ $t("chequeY") }}</span><input v-model.number="sel.y" type="number" step="0.5" /></label>
                  <label class="cs-field"><span>{{ $t("width") }}</span><input v-model.number="sel.w" type="number" step="1" min="5" /></label>
                  <label class="cs-field"><span>{{ $t("chequeFontSize") }}</span><input v-model.number="sel.size" type="number" step="0.5" min="5" max="30" /></label>
                  <label class="cs-field"><span>{{ $t("chequeSpacing") }}</span><input v-model.number="sel.spacing" type="number" step="0.1" min="0" max="6" /></label>
                  <label class="cs-check"><input v-model="sel.bold" type="checkbox" />{{ $t("chequeBold") }}</label>
                </div>
                <div class="cs-grid2">
                  <label class="cs-field">
                    <span>{{ $t("chequePrefix") }}</span>
                    <input :value="sel.prefix || ''" type="text" maxlength="6" class="cs-aff" @input="setAffix('prefix', $event.target.value)" />
                  </label>
                  <label class="cs-field">
                    <span>{{ $t("chequeSuffix") }}</span>
                    <input :value="sel.suffix || ''" type="text" maxlength="6" class="cs-aff" @input="setAffix('suffix', $event.target.value)" />
                  </label>
                </div>
                <div class="cs-chips">
                  <button
                    v-for="m in affixMarks"
                    :key="m.key"
                    type="button"
                    :class="{ on: (sel.prefix || '') === m.val && (sel.suffix || '') === m.val }"
                    @click="setAffix('prefix', m.val); setAffix('suffix', m.val)"
                  >
                    {{ m.label }}
                  </button>
                </div>
                <div class="cs-seg">
                  <button v-for="a in aligns" :key="a.key" type="button" :class="{ on: sel.align === a.key }" @click="sel.align = a.key">
                    <v-icon size="16">{{ a.icon }}</v-icon>
                  </button>
                </div>
                <p class="cs-hint"><v-icon size="14">mdi-keyboard-outline</v-icon>{{ $t("chequeKeysHint") }}</p>
              </div>
            </template>

            <template v-else>
              <div class="cs-group">
                <div class="cs-label">{{ $t("chequeLeafSize") }} (mm)</div>
                <div class="cs-grid2">
                  <label class="cs-field"><span>{{ $t("width") }}</span><input v-model.number="tpl.page.w" type="number" step="0.5" min="50" /></label>
                  <label class="cs-field"><span>{{ $t("height") }}</span><input v-model.number="tpl.page.h" type="number" step="0.5" min="30" /></label>
                </div>
              </div>
              <div class="cs-group">
                <div class="cs-label">{{ $t("chequePrinterOffset") }} (mm)</div>
                <p class="cs-hint">{{ $t("chequePrinterOffsetHint") }}</p>
                <div class="cs-grid2">
                  <label class="cs-field"><span>{{ $t("chequeX") }}</span><input v-model.number="tpl.page.offsetX" type="number" step="0.5" /></label>
                  <label class="cs-field"><span>{{ $t("chequeY") }}</span><input v-model.number="tpl.page.offsetY" type="number" step="0.5" /></label>
                </div>
              </div>
              <div class="cs-group">
                <label class="cs-check cs-check--head"><input v-model="tpl.guide.show" type="checkbox" />{{ $t("chequeGuide") }}</label>
                <p class="cs-hint">{{ $t("chequeGuideHint") }}</p>
                <template v-if="tpl.guide.show">
                  <div class="cs-grid2">
                    <label class="cs-field"><span>{{ $t("width") }} (mm)</span><input v-model.number="tpl.guide.w" type="number" step="0.5" min="20" /></label>
                    <label class="cs-field"><span>{{ $t("height") }} (mm)</span><input v-model.number="tpl.guide.h" type="number" step="0.5" min="10" /></label>
                    <label class="cs-field"><span>{{ $t("chequeX") }}</span><input v-model.number="tpl.guide.x" type="number" step="0.5" /></label>
                    <label class="cs-field"><span>{{ $t("chequeY") }}</span><input v-model.number="tpl.guide.y" type="number" step="0.5" /></label>
                  </div>
                  <button type="button" class="cs-link" @click="fitGuide">
                    <v-icon size="14">mdi-fit-to-page-outline</v-icon>{{ $t("chequeGuideFit") }}
                  </button>
                  <div class="cs-label cs-label--row">
                    {{ $t("chequeGuideOpacity") }} <b>{{ faNum(Math.round(tpl.guide.opacity * 100)) }}٪</b>
                  </div>
                  <input v-model.number="tpl.guide.opacity" type="range" min="0.1" max="1" step="0.05" class="cs-range" />
                </template>
              </div>
              <div class="cs-group">
                <div class="cs-label">{{ $t("chequeFont") }}</div>
                <div class="cs-chips">
                  <button v-for="f in fonts" :key="f.id" type="button" :class="{ on: tpl.theme.fontId === f.id }" @click="tpl.theme.fontId = f.id">
                    {{ f.label }}
                  </button>
                </div>
                <label class="cs-check"><input v-model="tpl.theme.faDigits" type="checkbox" />{{ $t("printFaDigits") }}</label>
              </div>
            </template>
          </div>
        </aside>

        <!-- Preview -->
        <section ref="stage" class="cs-stage">
          <div class="cs-zoom">
            <button type="button" @click="zoomBy(-0.1)"><v-icon size="16">mdi-minus</v-icon></button>
            <span>{{ faNum(Math.round(zoom * 100)) }}٪</span>
            <button type="button" @click="zoomBy(0.1)"><v-icon size="16">mdi-plus</v-icon></button>
          </div>
          <div class="cs-canvas" :style="canvasStyle">
            <iframe
              ref="frame"
              class="cs-frame"
              :srcdoc="previewHtml"
              :style="frameStyle"
              title="cheque"
              @load="onFrameLoad"
            />
          </div>
          <div class="cs-note">
            <v-icon size="14">mdi-information-outline</v-icon>{{ $t("chequeGuideNotPrinted") }}
          </div>
        </section>
      </div>

      <!-- Save as -->
      <v-dialog v-model="nameDialog" max-width="380">
        <div class="cs cs-name" :class="{ 'theme--dark': $vuetify.theme.isDark }" dir="rtl">
          <div class="cs-label">{{ $t("printSaveAs") }}</div>
          <label class="cs-field">
            <span>{{ $t("name") }}</span>
            <input ref="nameInput" v-model="newName" type="text" @keyup.enter="saveAs" />
          </label>
          <label v-if="canManage" class="cs-check"><input v-model="newCompany" type="checkbox" />{{ $t("chequeForCompany") }}</label>
          <div class="cs-name__actions">
            <button type="button" class="cs-btn cs-btn--primary" :disabled="!newName.trim() || busy" @click="saveAs">{{ $t("Save") }}</button>
            <button type="button" class="cs-btn" @click="nameDialog = false">{{ $t("cancel") }}</button>
          </div>
        </div>
      </v-dialog>
    </div>
  </v-dialog>
</template>

<script>
import * as store from "./storage";
import { FONTS } from "./fonts";
import { renderCheque, normalizeCheque, builtInChequeTemplates, chequeDigits } from "./cheque";
import guideImage from "@/assets/print/cheque-guide.png";

var MM = 3.7795; // px per mm at 96dpi
var PAD = 16; // px around the leaf inside the preview frame

export default {
  name: "ChequeStudio",
  props: {
    value: { type: Boolean, default: false },
    // Templates are stored per report key (e.g. "cheque-pay").
    report: { type: String, required: true },
    // [{ key, label, value(cheque) }]
    fields: { type: Array, required: true },
    // Cheques to print, one leaf each (the first one is shown in the preview).
    cheques: { type: Array, default: () => [] },
    subtitle: { type: String, default: "" },
  },
  data: () => ({
    tpl: null,
    tplScope: "builtin",
    savedJson: "",
    tab: "fields",
    selected: "",
    previewHtml: "",
    zoom: 1,
    userZoomed: false,
    stageWidth: 0,
    printing: false,
    busy: false,
    error: "",
    storeCtx: { source: "local", report: "" },
    canManage: false,
    userTemplates: [],
    companyTemplates: [],
    company: { defaultId: null, lockedIds: [], allowUserTemplates: true },
    nameDialog: false,
    newName: "",
    newCompany: false,
    renderTimer: null,
  }),
  computed: {
    rootStyle() {
      var theme = this.$vuetify.theme;
      var t = theme.themes[theme.isDark ? "dark" : "light"] || {};
      var p = typeof t.primary === "string" ? t.primary : "#1976d2";
      return { "--cs-primary": p, "--cs-primary-soft": p + "1a" };
    },
    guideUrl() {
      try {
        return new URL(guideImage, window.location.href).href;
      } catch (e) {
        return guideImage;
      }
    },
    fonts() {
      return FONTS;
    },
    aligns() {
      return [
        { key: "start", icon: "mdi-format-align-right" },
        { key: "center", icon: "mdi-format-align-center" },
        { key: "end", icon: "mdi-format-align-left" },
      ];
    },
    // Marks around the value (e.g. #123,000#) — none or a common one.
    affixMarks() {
      return [
        { key: "none", val: "", label: this.$t("chequeAffixNone") },
        { key: "hash", val: "#", label: "#" },
        { key: "star", val: "*", label: "*" },
        { key: "eq", val: "=", label: "=" },
        { key: "dash", val: "-", label: "-" },
      ];
    },
    sel() {
      return this.tpl && this.selected ? this.tpl.fields[this.selected] : null;
    },
    templateGroups() {
      var g = [
        { key: "company", label: this.$t("printGroupCompany"), items: this.companyTemplates },
        { key: "user", label: this.$t("printGroupMine"), items: this.userTemplates },
        { key: "builtin", label: this.$t("printGroupBuiltIn"), items: builtInChequeTemplates() },
      ];
      return g.filter(function (x) { return x.items.length; });
    },
    tplKey() {
      return this.tpl ? this.tplScope + ":" + this.tpl.id : "";
    },
    dirty() {
      return !!this.tpl && JSON.stringify(this.tpl) !== this.savedJson;
    },
    canSave() {
      if (!this.tpl || !this.dirty) return false;
      if (this.tplScope === "user") return true;
      return this.tplScope === "company" && this.canManage;
    },
    pageCount() {
      return 1; // the designer shows the first cheque only
    },
    frameW() {
      return this.tpl ? this.tpl.page.w * MM + PAD * 2 : 0;
    },
    frameH() {
      return this.tpl ? this.tpl.page.h * MM + PAD * 2 : 0;
    },
    frameStyle() {
      return {
        width: this.frameW + "px",
        height: this.frameH + "px",
        transform: "scale(" + this.zoom + ")",
      };
    },
    canvasStyle() {
      return { width: this.frameW * this.zoom + "px", height: this.frameH * this.zoom + "px" };
    },
  },
  watch: {
    value(open) {
      if (open) this.open();
    },
    tpl: {
      deep: true,
      handler() {
        this.scheduleRender();
      },
    },
    selected() {
      this.scheduleRender();
    },
    cheques() {
      this.scheduleRender();
    },
  },
  mounted() {
    window.addEventListener("resize", this.fit);
    window.addEventListener("keydown", this.onKey);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.fit);
    window.removeEventListener("keydown", this.onKey);
    this.detachFrame();
  },
  methods: {
    faNum(v) {
      return chequeDigits(v, String(this.$i18n && this.$i18n.locale).indexOf("fa") === 0);
    },
    tplName(t) {
      return t.builtIn ? this.$t(t.name) : t.name || this.$t("printUntitled");
    },
    fieldLabel(key) {
      var f = this.fields.find(function (x) { return x.key === key; });
      return f ? f.label : key;
    },
    sampleOf(f) {
      var c = this.cheques[0];
      var v = c && f.value ? f.value(c) : "";
      return v == null || v === "" ? "—" : this.faNum(v);
    },
    // ---- templates ----
    open() {
      var app = this;
      app.error = "";
      app.userZoomed = false;
      if (!app.tpl) app.useTemplate(builtInChequeTemplates()[0], "builtin", true);
      setTimeout(app.fit, 60);
      setTimeout(app.fit, 350);
      app.busy = true;
      store
        .load(app, app.report, app.$store.state.admin === "active")
        .then(function (r) {
          app.storeCtx = { source: r.source, report: app.report };
          app.canManage = r.canManage;
          app.company = r.settings;
          app.userTemplates = r.user;
          app.companyTemplates = r.company;
          if (app.dirty) return;
          var last = store.lastUsed(app.report);
          var all = [].concat(
            r.company.map(function (t) { return { t: t, s: "company" }; }),
            r.user.map(function (t) { return { t: t, s: "user" }; }),
            builtInChequeTemplates().map(function (t) { return { t: t, s: "builtin" }; })
          );
          var pick =
            all.find(function (x) { return String(x.t.id) === String(last); }) ||
            all.find(function (x) { return x.s === "company" && x.t.id === r.settings.defaultId; }) ||
            all[all.length - builtInChequeTemplates().length];
          app.useTemplate(pick.t, pick.s);
        })
        .catch(function (err) {
          app.error = store.errorMessage(err) || app.$t("printLoadError");
        })
        .finally(function () {
          app.busy = false;
        });
    },
    useTemplate(t, scope, silent) {
      var copy = normalizeCheque(t);
      copy.id = t.id;
      copy.name = t.name;
      copy.builtIn = !!t.builtIn;
      this.tpl = copy;
      this.tplScope = scope;
      this.savedJson = JSON.stringify(copy);
      if (!silent) store.lastUsed(this.report, t.id);
      this.$nextTick(this.fit);
    },
    pickTemplate(key) {
      var i = key.indexOf(":");
      var scope = key.slice(0, i);
      var id = key.slice(i + 1);
      var group = this.templateGroups.find(function (g) { return g.key === scope; });
      var t = group && group.items.find(function (x) { return String(x.id) === id; });
      if (t) this.useTemplate(t, scope);
    },
    askName() {
      this.newName = this.tpl && !this.tpl.builtIn ? this.tpl.name : "";
      this.newCompany = false;
      this.nameDialog = true;
      this.$nextTick(() => this.$refs.nameInput && this.$refs.nameInput.focus());
    },
    persist(scope, tpl) {
      var app = this;
      app.busy = true;
      app.error = "";
      return store
        .save(app, app.storeCtx, scope, tpl)
        .then(function (saved) {
          var list = scope === "company" ? app.companyTemplates : app.userTemplates;
          var i = list.findIndex(function (x) { return String(x.id) === String(saved.id); });
          if (i > -1) list.splice(i, 1, saved);
          else list.push(saved);
          app.useTemplate(saved, scope);
          return saved;
        })
        .catch(function (err) {
          app.error = store.errorMessage(err) || app.$t("printSaveError");
        })
        .finally(function () {
          app.busy = false;
        });
    },
    save() {
      this.persist(this.tplScope, Object.assign({}, this.tpl));
    },
    saveAs() {
      var name = this.newName.trim();
      if (!name) return;
      var t = Object.assign({}, JSON.parse(JSON.stringify(this.tpl)), { id: null, name: name, builtIn: false });
      this.nameDialog = false;
      this.persist(this.newCompany && this.canManage ? "company" : "user", t);
    },
    fitGuide() {
      this.tpl.guide.w = this.tpl.page.w;
      this.tpl.guide.h = this.tpl.page.h;
      this.tpl.guide.x = 0;
      this.tpl.guide.y = 0;
    },
    // ---- preview ----
    scheduleRender() {
      clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(this.render, 60);
    },
    render() {
      if (!this.tpl) return;
      var r = renderCheque({
        template: this.tpl,
        fields: this.fields,
        cheques: this.cheques.slice(0, 1),
        preview: true,
        guideUrl: this.guideUrl,
        selected: this.selected,
      });
      this.previewHtml = r.html.replace("</style>", "body{padding:" + PAD + "px}.pc-page{margin:0}</style>");
    },
    fit() {
      var stage = this.$refs.stage;
      if (!stage || !this.tpl || this.userZoomed) return;
      var w = stage.clientWidth - 48;
      if (w < 100) return;
      this.zoom = Math.max(0.5, Math.min(2.4, Math.floor((w / this.frameW) * 20) / 20));
    },
    zoomBy(d) {
      this.userZoomed = true;
      this.zoom = Math.max(0.4, Math.min(3, Math.round((this.zoom + d) * 100) / 100));
    },
    setAffix(which, v) {
      if (!this.sel) return;
      this.$set(this.sel, which, v);
    },
    select(key) {
      this.selected = key;
    },
    // Drag / resize inside the preview iframe (same origin via srcdoc).
    // Coordinates inside the frame are unscaled (the frame itself is scaled),
    // so 1 mm = MM px there.
    onFrameLoad() {
      this.detachFrame();
      var doc = this.$refs.frame && this.$refs.frame.contentDocument;
      if (!doc) return;
      this.frameDoc = doc;
      doc.addEventListener("pointerdown", this.onDown);
      doc.addEventListener("keydown", this.onKey);
    },
    detachFrame() {
      if (!this.frameDoc) return;
      this.frameDoc.removeEventListener("pointerdown", this.onDown);
      this.frameDoc.removeEventListener("keydown", this.onKey);
      this.frameDoc = null;
    },
    onDown(e) {
      var el = e.target.closest && e.target.closest(".pc-f");
      if (!el) {
        this.selected = "";
        return;
      }
      e.preventDefault();
      var key = el.getAttribute("data-key");
      var f = this.tpl.fields[key];
      var rect = el.getBoundingClientRect();
      var drag = {
        el: el,
        key: key,
        sx: e.clientX,
        sy: e.clientY,
        x: f.x,
        y: f.y,
        w: f.w,
        resize: e.clientX - rect.left < 7,
        moved: false,
      };
      el.classList.add("pc-f--sel");
      var doc = this.frameDoc;
      var move = (ev) => {
        var dx = (ev.clientX - drag.sx) / MM;
        var dy = (ev.clientY - drag.sy) / MM;
        if (Math.abs(dx) + Math.abs(dy) > 0.2) drag.moved = true;
        if (drag.resize) {
          var w = Math.max(5, drag.w - dx);
          el.style.left = drag.x + (drag.w - w) + "mm";
          el.style.width = w + "mm";
        } else {
          el.style.left = drag.x + dx + "mm";
          el.style.top = drag.y + dy + "mm";
        }
        drag.dx = dx;
        drag.dy = dy;
      };
      var up = () => {
        doc.removeEventListener("pointermove", move);
        doc.removeEventListener("pointerup", up);
        var snap = function (v) { return Math.round(v * 2) / 2; };
        if (drag.moved) {
          if (drag.resize) {
            var w = snap(Math.max(5, drag.w - drag.dx));
            f.x = snap(drag.x + (drag.w - w));
            f.w = w;
          } else {
            f.x = snap(drag.x + drag.dx);
            f.y = snap(drag.y + drag.dy);
          }
        }
        this.selected = key;
      };
      doc.addEventListener("pointermove", move);
      doc.addEventListener("pointerup", up);
    },
    // Arrow keys nudge the selected field 0.5 mm (Shift: 2 mm).
    onKey(e) {
      if (!this.value || !this.sel || this.nameDialog) return;
      var tag = (e.target && e.target.tagName) || "";
      if (/INPUT|SELECT|TEXTAREA/.test(tag)) return;
      var step = e.shiftKey ? 2 : 0.5;
      var d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
      if (!d) return;
      e.preventDefault();
      this.sel.x = Math.round((this.sel.x + d[0]) * 10) / 10;
      this.sel.y = Math.round((this.sel.y + d[1]) * 10) / 10;
    },
    // ---- print (no guide image) ----
    print() {
      var app = this;
      app.printing = true;
      var r = renderCheque({ template: app.tpl, fields: app.fields, cheques: app.cheques, preview: false });
      var frame = document.createElement("iframe");
      frame.style.cssText = "position:fixed;width:0;height:0;border:0;left:-9999px";
      document.body.appendChild(frame);
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        setTimeout(function () {
          frame.contentWindow.focus();
          frame.contentWindow.print();
          app.printing = false;
          setTimeout(function () { frame.remove(); }, 1500);
        }, 200);
      };
      var doc = frame.contentDocument;
      doc.open();
      doc.write(r.html);
      doc.close();
      if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(finish);
      setTimeout(finish, 3000);
    },
    close() {
      this.$emit("input", false);
    },
  },
};
</script>

<style scoped>
.cs {
  --cs-bg: #f3f5f9;
  --cs-surface: #fff;
  --cs-line: rgba(15, 23, 42, 0.08);
  --cs-muted: rgba(15, 23, 42, 0.6);
  --cs-text: #1e293b;
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--cs-bg);
  color: var(--cs-text);
}
.cs.theme--dark {
  --cs-bg: #121212;
  --cs-surface: #1e1e1e;
  --cs-line: rgba(255, 255, 255, 0.1);
  --cs-muted: rgba(255, 255, 255, 0.6);
  --cs-text: #e2e8f0;
}
.cs-head {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: var(--cs-surface);
  border-bottom: 1px solid var(--cs-line);
}
.cs-head__icon {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--cs-primary-soft);
  flex-shrink: 0;
}
.cs-head__icon .v-icon {
  color: var(--cs-primary);
}
.cs-head__titles {
  flex: 1 1 auto;
  min-width: 0;
}
.cs-head__title {
  font-weight: 700;
}
.cs-head__sub {
  font-size: 0.78rem;
  color: var(--cs-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cs-head__actions {
  display: flex;
  gap: 6px;
}
.cs-select {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.cs-select select {
  appearance: none;
  height: 34px;
  min-width: 200px;
  padding: 0 12px 0 30px;
  border-radius: 6px;
  border: 1px solid var(--cs-line);
  background: var(--cs-bg);
  color: inherit;
  font: inherit;
  font-size: 0.85rem;
}
.cs-select .v-icon {
  position: absolute;
  left: 8px;
  pointer-events: none;
  color: var(--cs-muted);
}
.cs-btn {
  height: 34px;
  padding: 0 12px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(0, 0, 0, 0.05);
  color: inherit;
  font-size: 0.85rem;
  white-space: nowrap;
}
.cs.theme--dark .cs-btn {
  background: rgba(255, 255, 255, 0.08);
}
.cs-btn .v-icon {
  color: inherit;
}
.cs-btn:disabled {
  opacity: 0.45;
}
.cs-btn--primary {
  background: var(--cs-primary) !important;
  color: #fff;
}
.cs-btn--icon {
  width: 34px;
  padding: 0;
  justify-content: center;
}
.cs-alert {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px 16px 0;
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(220, 38, 38, 0.1);
  color: #b91c1c;
  font-size: 0.82rem;
}
.cs-alert .v-icon {
  color: inherit;
}
.cs-main {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
}
.cs-side {
  flex: 0 0 320px;
  display: flex;
  flex-direction: column;
  background: var(--cs-surface);
  border-inline-end: 1px solid var(--cs-line);
  min-height: 0;
}
.cs-tabs {
  flex: 0 0 auto;
  display: flex;
  gap: 4px;
  padding: 8px;
  border-bottom: 1px solid var(--cs-line);
}
.cs-tabs button {
  flex: 1 1 0;
  height: 34px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.83rem;
  color: var(--cs-muted);
}
.cs-tabs button .v-icon {
  color: inherit;
}
.cs-tabs button.on {
  background: var(--cs-primary-soft);
  color: var(--cs-primary);
  font-weight: 600;
}
.cs-panel {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px;
}
.cs-panel > * {
  flex-shrink: 0;
}
.cs-hint {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  font-size: 0.75rem;
  color: var(--cs-muted);
  margin: 0 0 8px;
  line-height: 1.6;
}
.cs-hint .v-icon {
  color: inherit;
  margin-top: 2px;
}
.cs-fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}
.cs-frow {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  border: 1px solid var(--cs-line);
  cursor: pointer;
  font-size: 0.83rem;
}
.cs-frow.on {
  border-color: var(--cs-primary);
  background: var(--cs-primary-soft);
}
.cs-frow__label {
  flex: 0 0 auto;
  font-weight: 600;
}
.cs-frow__val {
  flex: 1 1 auto;
  min-width: 0;
  text-align: end;
  color: var(--cs-muted);
  font-size: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cs-switch {
  position: relative;
  flex: 0 0 auto;
  width: 30px;
  height: 18px;
}
.cs-switch input {
  position: absolute;
  opacity: 0;
  inset: 0;
  margin: 0;
  cursor: pointer;
  z-index: 1;
}
.cs-switch i {
  position: absolute;
  inset: 0;
  border-radius: 9px;
  background: rgba(100, 116, 139, 0.35);
  transition: background 0.15s;
}
.cs-switch i::after {
  content: "";
  position: absolute;
  top: 2px;
  right: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.15s;
}
.cs-switch input:checked + i {
  background: var(--cs-primary);
}
.cs-switch input:checked + i::after {
  transform: translateX(-12px);
}
.cs-group {
  padding: 12px 0;
  border-top: 1px solid var(--cs-line);
}
.cs-label {
  font-size: 0.8rem;
  font-weight: 700;
  margin-bottom: 8px;
}
.cs-label--row {
  display: flex;
  justify-content: space-between;
  margin: 10px 0 4px;
}
.cs-grid2,
.cs-grid3 {
  display: grid;
  gap: 8px;
  margin-bottom: 8px;
}
.cs-grid2 {
  grid-template-columns: 1fr 1fr;
}
.cs-grid3 {
  grid-template-columns: 1fr 1fr 1fr;
}
.cs-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-size: 0.72rem;
  color: var(--cs-muted);
}
.cs-field input {
  height: 32px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid var(--cs-line);
  background: var(--cs-bg);
  color: var(--cs-text);
  font: inherit;
  font-size: 0.85rem;
  direction: ltr;
  text-align: center;
  width: 100%;
}
.cs-field input[type="text"] {
  direction: rtl;
  text-align: start;
}
.cs-field input.cs-aff {
  direction: ltr;
  text-align: center;
}
.cs-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  align-self: end;
  height: 32px;
}
.cs-check--head {
  font-weight: 700;
  height: auto;
  margin-bottom: 4px;
}
.cs-seg {
  display: inline-flex;
  border: 1px solid var(--cs-line);
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 8px;
}
.cs-seg button {
  width: 38px;
  height: 30px;
  color: var(--cs-muted);
}
.cs-seg button .v-icon {
  color: inherit;
}
.cs-seg button.on {
  background: var(--cs-primary-soft);
  color: var(--cs-primary);
}
.cs-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
.cs-chips button {
  height: 30px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid var(--cs-line);
  font-size: 0.8rem;
  color: inherit;
}
.cs-chips button.on {
  border-color: var(--cs-primary);
  background: var(--cs-primary-soft);
  color: var(--cs-primary);
}
.cs-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.78rem;
  color: var(--cs-primary);
}
.cs-link .v-icon {
  color: inherit;
}
.cs-range {
  width: 100%;
  accent-color: var(--cs-primary);
}
.cs-stage {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px 24px;
}
.cs-zoom {
  position: absolute;
  top: 12px;
  left: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px;
  border-radius: 6px;
  background: var(--cs-surface);
  border: 1px solid var(--cs-line);
  font-size: 0.78rem;
}
.cs-zoom button {
  width: 28px;
  height: 28px;
  border-radius: 6px;
}
.cs-zoom span {
  min-width: 44px;
  text-align: center;
}
.cs-canvas {
  position: relative;
  flex: 0 0 auto;
}
.cs-frame {
  position: absolute;
  top: 0;
  left: 0;
  border: 0;
  transform-origin: 0 0;
  background: transparent;
}
.cs-note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 14px;
  font-size: 0.75rem;
  color: var(--cs-muted);
}
.cs-note .v-icon {
  color: inherit;
}
.cs-name {
  height: auto;
  padding: 16px;
  border-radius: 6px;
  gap: 12px;
}
.cs-name__actions {
  display: flex;
  gap: 8px;
}
@media (max-width: 799px) {
  .cs-main {
    flex-direction: column-reverse;
  }
  .cs-side {
    flex: 0 0 auto;
    max-height: 48vh;
    border-inline-end: 0;
    border-top: 1px solid var(--cs-line);
  }
  .cs-stage {
    padding: 48px 12px 12px;
  }
  .cs-btn__text,
  .cs-select {
    display: none;
  }
  .cs-btn {
    width: 34px;
    padding: 0;
    justify-content: center;
  }
}
</style>
